import { Check, X } from 'lucide-react';
import { BubbleMenu } from '@tiptap/react/menus';
import type { Editor } from '@tiptap/react';
import { Btn } from '@/components/editor-chrome/ui';

export interface RedlineBubbleMenuProps {
  editor: Editor | null;
  /** Optional fixed pluginKey so multiple BubbleMenus can coexist on one editor. */
  pluginKey?: string;
}

/**
 * Floating Accept / Reject toolbar for the `redline` Mark.
 *
 * Shown only when the current selection sits inside a `redline` mark. Accept
 * unwraps the mark (keeping the new text); Reject restores the original text
 * stored in the mark's `oldText` attribute. Both ops run through the editor's
 * command chain so undo/redo capture them as a single transaction.
 */
export function RedlineBubbleMenu({ editor, pluginKey = 'redlineBubbleMenu' }: RedlineBubbleMenuProps) {
  if (!editor) return null;

  return (
    <BubbleMenu
      editor={editor}
      pluginKey={pluginKey}
      updateDelay={0}
      shouldShow={({ editor: ed }) => ed.isEditable && ed.isActive('redline')}
      options={{
        strategy: 'fixed',
        placement: 'top',
        offset: 8,
        flip: true,
        shift: { padding: 8 },
      }}
      className="ed-redline-bubble"
    >
      <div
        role="toolbar"
        aria-label="Redline accept or reject"
        className="flex items-center gap-1 rounded-md border border-border bg-popover px-1.5 py-1 text-popover-foreground shadow-lg"
        // Preserve selection on click — BubbleMenu floats above the doc and
        // mousedown there would otherwise blur and clear the selection.
        onMouseDown={(e) => e.preventDefault()}
      >
        <Btn
          variant="primary"
          size="sm"
          onClick={() => editor.chain().focus().acceptRedline().run()}
          aria-label="Accept suggestion"
          icon={<Check className="h-3 w-3" aria-hidden="true" />}
        >
          Accept
        </Btn>
        <Btn
          variant="ghost"
          size="sm"
          onClick={() => editor.chain().focus().rejectRedline().run()}
          aria-label="Reject suggestion"
          icon={<X className="h-3 w-3" aria-hidden="true" />}
          className="text-destructive border-destructive/40 hover:bg-destructive/5"
        >
          Reject
        </Btn>
      </div>
    </BubbleMenu>
  );
}

export default RedlineBubbleMenu;
